import ContactsBoxesCollection  from "@/js/Contacts/Collections/ContactsBoxesCollection";
import SimpleCollectionList     from "@/js/Shared/Collections/SimpleCollectionList";
import IdentitiesCollectionList from "@/js/Settings/Identities/Collections/IdentitiesCollectionList";
import IdentitiesItem           from "@/js/Settings/Identities/Collections/IdentitiesItem";
import IdentitiesController     from "@/js/Settings/Identities/IdentitiesController";
import ContactsController       from "@/js/Contacts/ContactsController";
import ContactItem from "@/js/Contacts/Entities/ContactItem";

export {ComposeItem as default};

/**
* Compose Item
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class ComposeItem {
  /**
   * Compose id
   *
   * An unique identidier for each compose instance
   *
   * @public
   *
   * @type {String|undefined}
   *
   * @since 7.0.0
   */
  id;

  /**
   * An hash to prevent message modification
   *
   * @public
   *
   * @type {String}
   *
   * @since 7.0.0
   */
  hmac;

  /**
   * A binary representation of all compose field values
   *
   * @public
   *
   * @type {Number}
   *
   * @since 7.0.0
   */
  hash;

  /**
   * Last compose hash that was saved as a draft
   *
   * @public
   *
   * @type {Number}
   *
   * @since 7.0.0
   */
  hash_draft = 0;

  /**
   * Identities Collection
   *
   * @public
   *
   * @type {IdentitiesCollectionList}
   *
   * @since 7.0.0
   */
  identities;

  /**
   * Identity Selected
   *
   * @public
   *
   * @type {IdentitiesItem}
   *
   * @since 7.0.0
   */
  identity;

  /**
   * Defines the compose type (forward, reply, draft...)
   *
   * @public
   *
   * @type {String}
   *
   * @since 7.0.0
   */
  type;

  /**
   * Identity Selected Index
   *
   * @public
   *
   * @type {Number}
   *
   * @since 7.0.0
   */
  identity_index;

  /**
   * To Recipients
   *
   * @public
   *
   * @type {ContactsBoxesCollection}
   *
   * @since 7.0.0
   */
  to = new ContactsBoxesCollection();

  /**
   * CC Recipients
   *
   * @public
   *
   * @type {ContactsBoxesCollection}
   *
   * @since 7.0.0
   */
  cc = new ContactsBoxesCollection();

  /**
   * BCC Recipients
   *
   * @public
   *
   * @type {ContactsBoxesCollection}
   *
   * @since 7.0.0
   */
  bcc = new ContactsBoxesCollection();

  /**
   * Message priority
   *
   * @public
   *
   * @type {String}
   *
   * @since 7.0.0
   */
  priority = 'normal';

  /**
   * Ask for read receipt
   *
   * @public
   *
   * @type {Boolean}
   *
   * @since 7.0.0
   */
  read_receipt = false;

  /**
   * Composer subject
   *
   * @public
   *
   * @type {String}
   *
   * @since 7.0.0
   */
  subject;

  /**
   * Composer message
   *
   * @public
   *
   * @type {String}
   *
   * @since 7.0.0
   */
  message;

  /**
   * Sum of all attachments size combined
   *
   * @public
   *
   * @type {Number}
   *
   * @since 7.0.0
   */
  attachments_size = 0;

  /**
   * Count of all attachments of any type
   *
   * @public
   *
   * @type {Number}
   *
   * @since 7.0.0
   */
  attachments_total = 0;

  /**
   * Attachments inline
   *
   * @public
   *
   * @type {SimpleCollectionList}
   *
   * @since 7.0.0
   */
  attachments = new SimpleCollectionList();

  /**
   * Attachments inline
   *
   * @public
   *
   * @type {SimpleCollectionList}
   *
   * @since 7.0.0
   */
  attachments_inline = new SimpleCollectionList();

  /**
   * Attachments beeing uploaded Collection
   *
   * @public
   *
   * @type {SimpleCollectionList}
   *
   * @since 7.0.0
   */
  attachments_upload = new SimpleCollectionList();

  /**
   * Defines if the message has a signature
   *
   * @public
   *
   * @type {Boolean}
   *
   * @since 7.0.0
   */
  signature = false;

  /**
   * Generates an unique compose id
   *
   * @public
   *
   * @since 7.0.0
   */
  constructor() {
    this.id             = Date.now().toString(36) + Math.random().toString(36) + Math.random().toString(36);
    this.identities     = IdentitiesController.identities;
    this.setIdentity(this.identities.default);
    this.to.setContacts(ContactsController.contacts);
    this.cc.setContacts(ContactsController.contacts);
    this.bcc.setContacts(ContactsController.contacts);
    this.bcc.addPersonsByArray(this.identity.bccs);
  }

  /**
   * Sets the Identitiy index
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Number} id identity id
   *
   * @return {void}
   */
  setIdentity(id) {
    this.identity       = this.identities.get(id);
    this.identity_index = id;
  }

  /**
   * Generates a compose hash based on all compose items
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  generateHash() {
    let string = null;
    let hash   = 0;

    string += this.to.getEmailsAsString();
    string += this.cc.getEmailsAsString();
    string += this.bcc.getEmailsAsString();
    string += this.attachments_size + this.attachments_total;
    string += this.subject + this.message;

    for (let i = 0; i < string.length; i++) {
      let chr = string.charCodeAt(i);
      hash = ((hash << 5) - hash) + chr;
      hash |= 0; // Convert to 32bit integer
    }

    this.hash = hash;
  }

  /**
   * Sets the compose draft hash as the same value as the compose hash
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Boolean} returns true/false if value was modified
   */
  setHashDraft() {
    if (this.hash === this.hash_draft) {
      return false;
    }

    this.hash_draft = this.hash;
    return true;
  }

  /**
   * Switches the field of contact recipient
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ContactItem} contact contact to be switched
   * @param {String}      from   recipient field to be removed
   * @param {String}      to     recipient field to be added
   *
   * @return {void}
   */
  changeContactRecipient(contact, from, to) {
    this[from].delete(contact.id);
    this[to].add(contact);
  }
}